import {
  LandingSection,
  LandingSectionContent,
  type LandingSectionProps,
} from "@/components/landing-page/landing-page-section";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useScroll } from "@/hooks/use-scroll";
import { cn } from "@/lib/utils";
import XIcon from "lucide-solid/icons/x";
import {
  type ComponentProps,
  createMemo,
  createSignal,
  splitProps,
} from "solid-js";

const START_DATE = new Date("2020-01-01");

const getOrdinalSuffix = (num: number): string => {
  const lastTwo = num % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return "th";
  const lastOne = num % 10;
  if (lastOne === 1) return "st";
  if (lastOne === 2) return "nd";
  if (lastOne === 3) return "rd";
  return "th";
};

export function HeroSection(props: LandingSectionProps) {
  const [local, rest] = splitProps(props, ["class"]);

  const scroll = useScroll();

  return (
    <LandingSection class={cn("", local.class)} {...rest}>
      <div class="bg-geometric">
        <LandingSectionBanner />
        <LandingSectionContent class="text-balance md:min-h-[calc(3/5*100svh)]">
          <Card class="grow p-4 md:p-12 lg:p-20">
            <CardHeader class="mb-8 md:mb-12 lg:mb-12">
              <h1 class="mb-2.5 font-semibold text-4xl leading-tight tracking-normal md:text-5xl lg:mb-12 lg:text-6xl lg:leading-[1.1]">
                Unleash your leadership team coaching skills
              </h1>
              <h2 class="mb-3.5 font-semibold text-2xl text-muted-foreground tracking-tight md:text-3xl lg:text-4xl dark:text-orange-50">
                Transform your clients' teams from good to great
              </h2>
              <h3 class="text-lg text-muted-foreground md:text-xl dark:text-orange-50">
                Sign up for our{" "}
                <em class="whitespace-nowrap">
                  Team Coaching and Facilitation Workshop
                </em>{" "}
                designed exclusively for EOS® Implementers
              </h3>
            </CardHeader>
            <CardContent class="mb-8 grid w-full grid-cols-1 place-items-stretch gap-x-4 gap-y-2 md:w-max md:grid-cols-[auto_1fr_auto] md:place-items-center">
              <Button
                class="min-w-fit rounded-lg text-center font-semibold text-lg tracking-normal"
                size="lg"
                onClick={scroll({ to: "#info", offset: 200 })}
              >
                Learn more
              </Button>
              <p class="select-none place-self-center font-semibold text-muted-foreground/50">
                or
              </p>
              <Button
                class="min-w-fit rounded-lg bg-transparent text-center font-semibold text-cinnabar-500/90 text-lg tracking-normal ring-2 hover:bg-transparent hover:text-cinnabar-500"
                size="lg"
                onClick={scroll({ to: "#tickets" })}
              >
                Register now
              </Button>
            </CardContent>
          </Card>
        </LandingSectionContent>
      </div>
    </LandingSection>
  );
}

function LandingSectionBanner(props: ComponentProps<"div">) {
  const [local, rest] = splitProps(props, ["class"]);

  const [showBanner, setShowBanner] = createSignal(false);

  const yearNumber = createMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = START_DATE.getFullYear() + 1;
    const duration = currentYear - startYear;
    return `${duration}${getOrdinalSuffix(duration)}`;
  });

  return (
    <div
      bool:data-dismissed={showBanner()}
      class={cn(
        "slide-in-from-top-full relative w-full animate-in text-center delay-1000 duration-1000",
        "data-dismissed:slide-out-to-top-full data-dismissed:invisible data-dismissed:animate-out data-dismissed:delay-0 data-dismissed:duration-1000",
        "transition-all",
        "mt-(--header-height)",
        local.class,
      )}
      {...rest}
    >
      <span class="inline-flex min-h-10 w-full items-center justify-center bg-cinnabar-500 font-bold text-lg text-white">
        Now in our {yearNumber()} year! 🎉
        <span class="absolute right-2">
          <button
            type="button"
            class="inline-flex size-6 items-center justify-center rounded-full align-middle font-medium"
            onClick={() => setShowBanner(true)}
          >
            <span class="sr-only">Dismiss</span>
            <XIcon class="size-5 transition-transform hover:text-white/80" />
          </button>
        </span>
      </span>
    </div>
  );
}
